import { useEffect, useState, useRef } from 'react';
import styles from '../datepicker.module.css';

// Listen for click outside, click on button, click on input and esc
export const useOpen = (inputRef: React.RefObject<HTMLInputElement>) => {
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    const onClick = ({ target }: MouseEvent) => {
      if (!(target instanceof Element)) return;
      const isInput = inputRef.current?.contains(target);
      const isOutside = !target.closest(`.${styles.dategrid}`);
      const isButton =
        target.closest(`.${styles.suffix}`) ===
        inputRef.current?.nextElementSibling;

      if (isInput) setOpen(true);
      else if (isButton) setOpen(!open);
      else if (isOutside) setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(Boolean(event.preventDefault())); // Prevent minimize Safari with preventDefault
    };

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onEscape);

    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open, inputRef]);

  return [open, setOpen] as const;
};

// Helper to spy on the change of a value
export const useOnPropChange = <T>(next: T, fn: (next: T, prev: T) => void) => {
  const ref = useRef(next); // Using ref as this is remembered, but does not trigger re-render
  const prev = ref.current;

  // use useEffect to ensure proper state restoration
  useEffect(() => {
    if (next !== prev) fn((ref.current = next), prev);
  }, [next, fn, prev]);
};

// Helper to add multiple functions to same React event listener
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const combineOn = <T extends (...args: any[]) => void>(
  ...fns: Array<T | undefined>
) => {
  return (...args: Parameters<T>) => fns.forEach((fn) => fn && fn(...args));
};
